import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, CheckCircle, AlertCircle, FileSpreadsheet, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { financialAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const AdminUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setUploadResult(null);
      } else {
        setUploadResult({ success: false, message: 'Bitte eine Excel-Datei (.xlsx oder .xls) auswählen' });
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadResult({ success: false, message: 'Bitte eine Datei auswählen' });
      return;
    }

    try {
      setUploading(true);
      const result = await financialAPI.uploadExcel(file);
      
      setUploadResult({
        success: result.success,
        message: result.message,
        data: result.data
      });
      
      if (result.success) {
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Fehler beim Hochladen der Datei'
      });
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-black text-white py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-2xl font-black text-white tracking-tight">EQUIPPERS</h1>
          <ArrowUpRight className="w-5 h-5 text-[#FF4600]" strokeWidth={3} />
        </div>
        
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#FF4600]"></div>
            <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
              Admin
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight leading-none">
            Daten<br />
            <span className="text-[#FF4600]">Upload</span>
          </h2>
        </div>

        <Card className="bg-white/5 backdrop-blur-sm border border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-white uppercase tracking-wide">
              <FileSpreadsheet className="w-6 h-6 text-[#FF4600]" />
              Excel-Datei hochladen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="border-2 border-dashed border-white/20 rounded-none p-12 text-center hover:border-[#FF4600] transition-colors">
              <input
                data-testid="file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 text-white/40 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-lg font-bold text-white mb-2 uppercase tracking-wide">
                  {file ? file.name : 'Datei auswählen'}
                </p>
                <p className="text-sm text-white/50 uppercase tracking-widest">
                  .xlsx · .xls
                </p>
              </label>
            </div>

            <Button
              data-testid="upload-btn"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full bg-[#FF4600] hover:bg-[#ff5a1a] text-white font-bold uppercase tracking-widest rounded-none py-6 disabled:opacity-40"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verarbeite...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Hochladen
                </>
              )}
            </Button>

            {uploadResult && (
              <div
                className={`p-6 border-2 ${
                  uploadResult.success
                    ? 'bg-[#FF4600]/10 border-[#FF4600]'
                    : 'bg-red-500/10 border-red-500'
                }`}
              >
                <div className="flex items-start gap-3">
                  {uploadResult.success ? (
                    <CheckCircle className="w-6 h-6 text-[#FF4600] flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-bold uppercase tracking-wide text-white mb-1">
                      {uploadResult.message}
                    </p>
                    {uploadResult.success && uploadResult.data && (
                      <div className="text-sm text-white/80 space-y-1 mt-4">
                        <p className="font-bold uppercase tracking-widest text-[#FF4600] text-xs mb-2">
                          Extrahierte Daten · {uploadResult.data.year}
                        </p>
                        <ul className="space-y-1 ml-2">
                          <li>Einnahmen: <span className="font-bold">{formatCurrency(uploadResult.data.revenue)}</span></li>
                          <li>Ausgaben: <span className="font-bold">{formatCurrency(uploadResult.data.expenses)}</span></li>
                          <li>Ergebnis: <span className="font-bold text-[#FF4600]">{formatCurrency(uploadResult.data.result)}</span></li>
                        </ul>
                        <p className="mt-4 italic text-white/50">Weiterleitung...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white/5 border border-white/10 p-6">
              <h4 className="font-bold text-[#FF4600] mb-3 uppercase tracking-widest text-xs">Format-Hinweise</h4>
              <p className="text-sm text-white/70 mb-3 font-light">
                Die Excel-Datei sollte folgende Informationen enthalten:
              </p>
              <ul className="text-sm text-white/60 space-y-2 font-light">
                <li>· Spalte mit Beschreibungen (Einnahmen, Ausgaben, Ergebnis)</li>
                <li>· Spalte mit Beträgen in Euro</li>
                <li>· Das System erkennt automatisch die relevanten Zeilen</li>
              </ul>
            </div>

            <Button
              data-testid="back-btn"
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full bg-transparent border-2 border-white/20 text-white hover:bg-white hover:text-black font-bold uppercase tracking-widest rounded-none py-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminUpload;
